import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Integration } from "../models/Integration";
import { IntegrationService } from "../services/IntegrationService";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "./AuthContext";
import { getSubdomainFromUrl } from "../utils/url";

interface IntegrationContextProps {
  availableIntegrations: Integration[];
  integrations: Integration[];
  createIntegration: (
    fileData: Omit<
      Integration,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "updatedById"
    >,
  ) => Promise<string>;
  updateIntegration: (
    integrationId: string,
    fileData: Partial<
      Omit<
        Integration,
        "_id" | "createdAt" | "updatedAt" | "createdById" | "updatedById"
      >
    >,
  ) => Promise<void>;
  deleteIntegrationById: (integrationId: string) => void;
  currentIntegration?: Integration;
}

const IntegrationContext = createContext<IntegrationContextProps | undefined>(
  undefined,
);

export const useIntegration = (): IntegrationContextProps => {
  const context = useContext(IntegrationContext);
  if (!context) {
    throw new Error("useIntegration must be used within a IntegrationProvider");
  }
  return context;
};

export const IntegrationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [availableIntegrations, setAvailableIntegrations] = useState<
    Integration[]
  >([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    const docRef = query(collection(db, "integrations"));
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setAvailableIntegrations(
          docSnapshot.docs.map(
            (doc) => ({ _id: doc.id, ...doc.data() }) as Integration,
          ),
        );
      },
      (err) => {
        setError(`Failed to listen to integration changes: ${err.message}`);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const tenantId = getSubdomainFromUrl(window.location.href);
    const docRef = query(collection(db, "tenants", tenantId, "integrations"));
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => {
        setIntegrations(
          docSnapshot.docs.map(
            (doc) => ({ _id: doc.id, ...doc.data() }) as Integration,
          ),
        );
      },
      (err) => {
        setError(`Failed to listen to integration changes: ${err.message}`);
      },
    );
    return () => {
      unsubscribe();
    };
  }, [user]);

  const createIntegration = async (
    fileData: Omit<
      Integration,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "createdById"
      | "matterId"
      | "createdById"
      | "updatedById"
    >,
  ): Promise<string> => {
    const newProintegrationId = await IntegrationService.createIntegration({
      ...fileData,
    });
    return newProintegrationId;
  };

  const updateIntegration = async (
    integrationId: string,
    fileData: Partial<
      Omit<
        Integration,
        | "_id"
        | "createdAt"
        | "updatedAt"
        | "createdById"
        | "matterId"
        | "updatedById"
      >
    >,
  ) => {
    await IntegrationService.updateIntegration(integrationId, fileData);
  };

  const deleteIntegrationById = async (integrationId: string) => {
    await IntegrationService.deleteIntegrationById(integrationId);
  };

  return (
    <IntegrationContext.Provider
      value={{
        availableIntegrations,
        integrations,
        createIntegration,
        updateIntegration,
        deleteIntegrationById,
      }}
    >
      {children}
    </IntegrationContext.Provider>
  );
};
